import { createSelector } from "@reduxjs/toolkit";

import {
  ACTIVITY_READING,
  ACTIVITY_SUGGESTING,
  ACTIVITY_THINKING,
} from "./activityLabels";
import { carriedBelongsToPage, isConversationElsewhere } from "./pageIdentity";

export const selectVisiEverywherePage = (state) => state.visi.everywhere.page;

export const selectVisiEverywherePageOwner = (state) =>
  state.visi.everywhere.pageOwner ?? null;

export const selectIsVisiEverywhereOpen = (state) =>
  state.visi.everywhere.isOpen;

export const selectVisiEverywhereTurns = (state) => state.visi.everywhere.turns;

export const selectVisiEverywhereProgress = (state) =>
  state.visi.everywhere.progress;

export const selectVisiEverywhereSummary = (state) =>
  state.visi.everywhere.summary;

export const selectIsLoadingVisiEverywhereSummary = (state) =>
  state.visi.everywhere.isLoadingSummary;

export const selectIsSuggestingVisiEverywhereQuestions = (state) =>
  state.visi.everywhere.isSuggestingQuestions;

export const selectHasVisiEverywhereSummaryError = (state) =>
  state.visi.everywhere.hasSummaryError;

export const selectHasSkippedVisiEverywhereSummary = (state) =>
  state.visi.everywhere.hasSkippedSummary;

export const selectVisiEverywhereSummaryErrorMessage = (state) =>
  state.visi.everywhere.summaryErrorMessage;

export const selectVisiEverywhereSuggestionErrorMessage = (state) =>
  state.visi.everywhere.suggestionErrorMessage;

export const selectIsVisiEverywhereStreaming = (state) =>
  state.visi.everywhere.turns.some((turn) => turn.isStreaming);

export const selectVisiEverywhereFollowUpQuestions = (state) =>
  state.visi.everywhere.summary?.followUpQuestions || [];

export const selectIsVisiEverywhereRegistered = (state) =>
  Boolean(state.visi.everywhere.page?.pageId);

export const selectHasResolvedVisiEnabledPages = (state) =>
  state.visi.everywhere.hasResolvedEnabledPages === true;

export const selectIsVisiPageEnabled = (state, pageId) =>
  Boolean(pageId) &&
  state.visi.everywhere.enabledPages.some((page) => page.pageId === pageId);

export const selectCarriedVisiConversation = (state) =>
  state.visi.everywhere.carriedConversation;

export const selectCarriedVisiConversationId = (state) =>
  state.visi.everywhere.page
    ? (state.visi.everywhere.carriedConversation?.conversationId ?? null)
    : null;

export const selectCarriedVisiConversationIdForThisPage = (state) =>
  carriedBelongsToPage(
    state.visi.everywhere.carriedConversation,
    state.visi.everywhere.page
  )
    ? state.visi.everywhere.carriedConversation.conversationId
    : null;

export const selectVisiPageIdentityParams = (state, pageId) =>
  state.visi.everywhere.enabledPages.find((page) => page.pageId === pageId)
    ?.identityParams ?? null;

export const selectIsLoadingVisiEverywhereHistory = (state) =>
  state.visi.everywhere.isLoadingHistory;

export const selectHasResolvedVisiEverywhereHistory = (state) =>
  state.visi.everywhere.hasResolvedHistory;

export const selectCanRevealVisiEverywhereCitations = (state) =>
  state.visi.everywhere.canRevealCitations;

export const selectHasVisiEverywhereTurns = (state) =>
  state.visi.everywhere.turns.length > 0;

// A turn the server refused was never stored, so it leaves no chat behind to show a card for.
export const selectHasStoredVisiEverywhereTurns = (state) =>
  state.visi.everywhere.turns.some((turn) => !turn.error);

export const selectHasVisiEverywhereChat = (state) =>
  Boolean(state.visi.everywhere.conversationId) ||
  selectHasStoredVisiEverywhereTurns(state);

export const selectIsListingAllVisiConversationPages = (state) =>
  state.visi.everywhere.isListingAllPages;

export const selectAreListedVisiConversationsFromAllPages = (state) =>
  state.visi.everywhere.areListedConversationsFromAllPages;

export const selectVisiEverywhereConversationId = (state) =>
  state.visi.everywhere.conversationId;

export const selectVisiEverywhereConversationTitle = (state) =>
  state.visi.everywhere.conversationTitle;

export const selectShouldStartNewVisiConversation = (state) =>
  state.visi.everywhere.startNewConversation;

export const selectAbandonedConversationEpoch = (state) =>
  state.visi.everywhere.abandonedConversationEpoch ?? 0;

export const selectVisiEverywhereConversationPage = createSelector(
  [
    (state) => state.visi.everywhere.conversationPageId,
    (state) => state.visi.everywhere.conversationPageTitle,
    (state) => state.visi.everywhere.conversationPageParams,
  ],
  (pageId, pageTitle, pageParams) => ({ pageId, pageTitle, pageParams })
);

export const selectIsVisiEverywhereConversationElsewhere = (state) =>
  isConversationElsewhere(state.visi.everywhere.page, {
    pageId: state.visi.everywhere.conversationPageId,
    pageParams: state.visi.everywhere.conversationPageParams,
  });

export const selectIsNamingVisiConversation = (state) =>
  state.visi.everywhere.isNamingConversation;

export const selectVisiEverywhereConversations = (state) =>
  state.visi.everywhere.conversations;

export const selectIsLoadingVisiEverywhereConversations = (state) =>
  state.visi.everywhere.isLoadingConversations;

export const selectLatestVisiConversationsRequestId = (state) =>
  state.visi.everywhere.latestConversationsRequestId;

export const selectPendingVisiClear = (state) =>
  state.visi.everywhere.pendingClear;

/**
 * Whether a question would land in the chat currently being emptied. A clear outlives the page it
 * was started on, so the barrier follows the conversation rather than the panel.
 *
 * An unresolved conversation counts. Before history answers, the panel holds no id and the server
 * resolves the turn to whichever conversation is current on this page — which may be the one being
 * cleared. Only a deliberate New Chat is known to land somewhere else.
 */
export const selectIsClearingVisiConversation = (state) => {
  const everywhere = state.visi.everywhere;
  const pending = everywhere.pendingClear;
  if (!pending) {
    return false;
  }
  if (everywhere.conversationId != null) {
    return pending.conversationId === everywhere.conversationId;
  }
  return !everywhere.startNewConversation;
};

export const selectConversationMutationEpoch = (state) =>
  state.visi.everywhere.conversationMutationEpoch ?? 0;

export const selectVisiPageGeneration = (state) =>
  state.visi.everywhere.pageGeneration ?? 0;

export const selectVisiEverywhereContextGeneration = (state) =>
  state.visi.everywhere.contextGeneration ?? 0;

export const selectConversationMutationFor = (state, conversationId) =>
  state.visi.everywhere.conversationMutations?.[conversationId] ?? 0;

export const selectVisiEverywhereView = (state) => state.visi.everywhere.view;

export const selectVisiEverywhereActivity = (state) => {
  const everywhere = state.visi.everywhere;
  if (everywhere.isLoadingSummary) {
    return ACTIVITY_READING;
  }
  if (everywhere.isSuggestingQuestions) {
    return ACTIVITY_SUGGESTING;
  }
  if (everywhere.progress) {
    return everywhere.progress;
  }
  const lastTurn = everywhere.turns[everywhere.turns.length - 1];
  if (lastTurn?.isStreaming && !lastTurn.answer) {
    return ACTIVITY_THINKING;
  }
  return "";
};
